"use client";

import type { CSSProperties } from "react";
import { useMemo, useState } from "react";

import Image from "@/components/common/Image";
import { Heading } from "@/components/ui/Heading";

import { DEFENDR_CONSTRUCTION_SPECIFICATIONS } from "./draft";
import { ArrowRight, X } from "lucide-react";

export function ConstructionSpecifications({ block }: { block: any }) {
  const [activeId, setActiveId] = useState(
    DEFENDR_CONSTRUCTION_SPECIFICATIONS.hotspots[0]?.id ?? null,
  );

  const activeHotspot = useMemo(
    () =>
      DEFENDR_CONSTRUCTION_SPECIFICATIONS.hotspots.find(
        (hotspot) => hotspot.id === activeId,
      ) ?? null,
    [activeId],
  );

  return (
    <section
      className="bg-[#e2e2e2] text-black py-24 md:py-32 z-[1] relative overflow-hidden"
      id={"construction"}
    >
      <div className="container mx-auto px-gutter stack gap-10">
        <div className="max-w-[72ch] stack gap-4">
          <Heading as="h2" bottomBorder>
            {block?.title || DEFENDR_CONSTRUCTION_SPECIFICATIONS.title}
          </Heading>
          {/* <p className="max-w-[64ch] text-[#3f3a32]">
            {DEFENDR_CONSTRUCTION_SPECIFICATIONS.description}
          </p> */}
        </div>

        <div className="grid gap-24 lg:grid-cols-2 xl:items-start relative mt-12">
          <div className="stack gap-8">
            <h3 className="text-4xl font-medium">
              {DEFENDR_CONSTRUCTION_SPECIFICATIONS.eyebrow}
            </h3>
            <p>
              All components of the Forte Opening Solutions Attack Resistant
              Door System — metal frame, attack-resistant glass, ANSI/BHMA Grade
              1 hardware, solid-core wood door and metal vision kit— work
              together to provide the highest level of security in new
              construction or security-update retrofit projects. While Forte
              Opening Solutions Attack Resistant Doors can be installed in
              existing frames, the full system is required for the greatest
              degree of safety.
            </p>
            <p>
              To view the system components, click on the components to right to
              reveal full descriptions.{" "}
              <ArrowRight className="inline-block ml-2 relative -top-[4px]" />
            </p>
          </div>
          <div className="relative flex items-start gap-2">
            <div
              className="relative overflow-hidden z-0 transition-all duration-500"
              style={{ flex: activeHotspot ? "0 0 55%" : "0 0 100%" }}
            >
              <Image
                src={
                  "https://cdn.bfldr.com/QL33D4U/at/2n43t35hmjttxz6v28cqxgt/Door_Assembly.png"
                }
                alt="Defendr door system construction specifications"
                imgClassName="aspect-[2/1] max-h-[800px] w-full rounded-[1.5rem] object-contain relative z-0"
                cover={false}
                maxWidth="1600"
              />

              {DEFENDR_CONSTRUCTION_SPECIFICATIONS.hotspots.map(
                (hotspot, index) => (
                  <button
                    key={hotspot.id}
                    type="button"
                    onClick={() => setActiveId(hotspot.id)}
                    style={
                      {
                        left: `${hotspot.x - (activeHotspot ? 20 : 10)}%`,
                        top: `${hotspot.y}%`,
                        anchorName: `--hotspot-${hotspot.id}`,
                      } as CSSProperties
                    }
                    className={`absolute h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full border text-sm font-semibold transition-all z-10 ${
                      hotspot.id === activeId
                        ? "border-gold bg-gold text-black shadow-[0_0_0_8px_rgba(183,151,74,0.18)]"
                        : "border-gold bg-gold text-white hover:bg-black opacity-75 hover:opacity-100"
                    }`}
                    aria-label={`Show ${hotspot.title}`}
                    aria-pressed={hotspot.id === activeId}
                  >
                    {index + 1}
                  </button>
                ),
              )}
            </div>

            {activeHotspot ? (
              <div
                className="rounded-[2rem] text-black z-10 max-w-full flex-[0_0_40%] stack gap-2 mt-10 relative overflow-hidden fade-in-overlay"
                // style={
                //   {
                //     width: "40%",
                //     height: "auto",
                //     left: `anchor(--hotspot-${activeHotspot.id} right, 0%)`,
                //     right: 0,
                //     top: `34%`,
                //     positionAnchor: `--hotspot-${activeHotspot.id}`,
                //     translate: "1rem -50%",
                //   } as CSSProperties
                // }
              >
                <div
                  className="rounded-full border text-sm font-semibold border-gold bg-gold text-black w-[32px] h-[32px] flex items-center justify-center"
                  style={{ width: "40px", height: "40px" }}
                >
                  {activeHotspot.number}
                </div>
                <h3 className="font-hero text-[2rem] leading-tight max-w-full pr-4">
                  {activeHotspot.title}
                </h3>
                <p className="text-white/75 max-w-full">
                  {activeHotspot.description}
                </p>
                <button
                  type="button"
                  onClick={() => setActiveId(null)}
                  className="text-left p-0 text-gold flex items-center gap-2 mt-4 cursor-pointer"
                  aria-label={`Close ${activeHotspot.title} details`}
                >
                  Close <X />
                </button>

                {/* <div className="stack gap-4 border-t border-white/10 pt-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#d6c18b]">
                Specification links
              </p>
              <div className="grid gap-3">
                {DEFENDR_CONSTRUCTION_SPECIFICATIONS.links.map((link) => (
                  <div
                    key={link.title}
                    className="rounded-[1.25rem] border border-white/10 bg-white/[0.04] p-4"
                  >
                    <div className="stack gap-3">
                      <div>
                        <p className="font-semibold text-white">{link.title}</p>
                        <p className="mt-1 text-sm text-white/65">
                          {link.description}
                        </p>
                      </div>
                      <Button href={link.href} isLight>
                        Explore {link.title}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
